import { create } from 'zustand';
import { Skill as SkillApi } from '@/network/RestApi';

// Server skill template (mirrors backend/services/api/internal/skill/handler.go::tpl)
export interface SkillTemplate {
  id: number;
  code: string;
  name: string;
  class: 'warrior' | 'wizard' | 'archer';
  branch: string; // 'A' | 'B' | 'C' | 'master' (server) — kept generic
  tier: number;
  levelReq: number;
  maxLevel: number;
  isMagic: boolean;
  isMaster: boolean;
  baseFlat: number;
  growthFlat: number;
  multiplier: number;
  statScale: number;
  manaCost: number;   // base_mana
  cooldownMs: number; // base_cd_ms
  element: number;
  rangeM?: number;    // cast range in meters (1m ≈ 1 tile = 32px); 0 / undefined → use class default
  aoeRadiusM: number;
  maxTargets: number;
  defensePenPct: number;
  ccKind: string;
  ccChance: number;
  ccDurationMs: number;
  slowPct: number;
  dotPerSec: number;
  dotDurationMs: number;
  buffDurationMs: number;
  manaDrainPerS: number;
  isToggle: boolean;
  isPassive: boolean;
  description: string;
}

interface SkillStore {
  templates: SkillTemplate[];
  byId: Record<number, SkillTemplate>;
  loading: boolean;
  loaded: boolean;
  error?: string;
  // Learned skills for the current character: skillId -> level (>=1).
  learned: Record<number, number>;
  learnedCharId?: number;
  learnedLoaded: boolean;
  fetch: (force?: boolean) => Promise<SkillTemplate[]>;
  fetchLearned: (charId: number, force?: boolean) => Promise<Record<number, number>>;
  setLearnedLevel: (skillId: number, level: number) => void;
  byClass: (klass: SkillTemplate['class']) => SkillTemplate[];
}

let inflight: Promise<SkillTemplate[]> | null = null;
let inflightLearned: Promise<Record<number, number>> | null = null;

export const useSkillStore = create<SkillStore>((set, get) => ({
  templates: [],
  byId: {},
  loading: false,
  loaded: false,
  error: undefined,
  learned: {},
  learnedCharId: undefined,
  learnedLoaded: false,
  fetch: async (force = false) => {
    const st = get();
    if (st.loaded && !force) return st.templates;
    if (inflight) return inflight;
    set({ loading: true, error: undefined });
    inflight = (async () => {
      try {
        const r = await SkillApi.templates() as { templates?: SkillTemplate[] };
        const list = r.templates ?? [];
        const byId: Record<number, SkillTemplate> = {};
        for (const t of list) byId[t.id] = t;
        set({ templates: list, byId, loaded: true, loading: false });
        return list;
      } catch (e) {
        set({ loading: false, error: (e as Error).message });
        return [];
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  },
  fetchLearned: async (charId, force = false) => {
    const st = get();
    if (!force && st.learnedLoaded && st.learnedCharId === charId) return st.learned;
    if (inflightLearned) return inflightLearned;
    inflightLearned = (async () => {
      try {
        const r = await SkillApi.list(charId) as { skills?: { skillId: number; level: number }[] };
        const obj: Record<number, number> = {};
        (r.skills ?? []).forEach((s) => { obj[s.skillId] = s.level; });
        set({ learned: obj, learnedCharId: charId, learnedLoaded: true });
        return obj;
      } catch {
        set({ learned: {}, learnedCharId: charId, learnedLoaded: true });
        return {};
      } finally {
        inflightLearned = null;
      }
    })();
    return inflightLearned;
  },
  setLearnedLevel: (skillId, level) =>
    set((s) => ({ learned: { ...s.learned, [skillId]: level } })),
  byClass: (klass) => get().templates.filter((s) => s.class === klass),
}));

